import { AuthorizationError } from '@/app/common/components/AuthorizationError'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import PageLoader from '@/app/common/components/PageLoader'
import { useGetProgramCatalogCategories } from '@/queries/program_catalog/hooks/useGetProgramCatalogCategories'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import AdminAuthGuard from '../../admin-auth-guard'
import { useAddProduct } from '../configureStore/hooks/useAddProduct'
import { TransformedProduct } from '../ProductForm/add-product.schema'
import ProductForm from '../ProductForm/ProductForm'
import ProductAddedDialog from './ProductAddedDialog'

export default function AddProductPage() {
  const { id } = useSelector(selectSelectedProgram)
  const [dialogOpen, setDialogOpen] = useState(false)
  const params = useParams()
  const providerIdRaw = params.providerId
  if (!providerIdRaw) {
    throw new Error('Provider id is not provided in the URL parameters.')
  }
  const providerId = parseInt(providerIdRaw)
  if (isNaN(providerId)) {
    throw new Error('Provider id in the URL parameters is not a valid number.')
  }
  const programId = useSelector(selectSelectedProgram).id
  const systemCategoriesQuery = useGetProgramCatalogCategories(id)
  const { toast } = useToast()

  const addProductMutation = useAddProduct({
    providerId,
    programId,
    onSuccess: () => {
      setDialogOpen(true)
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'There was an error creating your product.',
        variant: 'destructive',
      })
    },
  })

  function handleSubmitProductForm(
    data: TransformedProduct,
    categories: ProgramCategory[]
  ) {
    let selectedProgramItem:
      | ProgramCategory['program_items'][number]
      | undefined
    let parentCategory: ProgramCategory | undefined
    for (const category of categories) {
      const found = category.program_items.find(
        (item) => item.id.toString() === data.program_item_id?.toString()
      )
      if (found) {
        selectedProgramItem = found
        parentCategory = category
        break
      }
    }

    if (!selectedProgramItem || !parentCategory) {
      toast({
        title: 'Error',
        description: 'There was an error creating your product.',
        variant: 'destructive',
      })
      return
    }

    const payload = {
      program_id: programId,
      payload: data,
      provider_id: providerId.toString(),
      global_item_category_id: parentCategory.global_item_category_id,
      global_item_id: selectedProgramItem.global_item_id,
      type: selectedProgramItem.global_item.type,
    }

    addProductMutation.mutate(payload)
  }

  return (
    <AdminAuthGuard
      requiredPermissions={['provider_management_edit']}
      fallbackComponent={<AuthorizationError />}
    >
      <div className="max-w-4xl w-full mx-auto">
        <PageLoader query={systemCategoriesQuery.query}>
          {(categories) => {
            return (
              <>
                <Card>
                  <CardHeader>Add Product</CardHeader>
                  <CardContent>
                    <ProductForm
                      isNewProduct={true}
                      mutationStatus={addProductMutation.status}
                      onSubmit={(data) => {
                        handleSubmitProductForm(data, categories)
                      }}
                      categories={categories}
                    />
                  </CardContent>
                </Card>
                {addProductMutation.status === 'success' && (
                  <ProductAddedDialog
                    open={dialogOpen}
                    providerId={providerId}
                  />
                )}
              </>
            )
          }}
        </PageLoader>
      </div>
    </AdminAuthGuard>
  )
}
